import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  message,
  Spin,
  Row,
  Col,
  Divider,
  Avatar,
  Progress,
  Alert,
} from "antd";
import {
  UploadOutlined,
  SaveOutlined,
  BankOutlined,
  LoadingOutlined,
  PlusOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  InstagramOutlined,
  FacebookOutlined,
} from "@ant-design/icons";
import axios from "axios";
import styled from "styled-components";
import { API_CONST } from "../../const";

const { TextArea } = Input;
const { Option } = Select;

const TabWrapper = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #014c75;
    margin-bottom: 24px;
  }

  .completion-banner {
    background: linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%);
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .completion-text {
    flex: 1;
  }

  .completion-title {
    font-weight: 600;
    color: #014c75;
    margin-bottom: 4px;
  }

  .completion-subtitle {
    font-size: 13px;
    color: #595959;
  }

  .logo-upload {
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 24px;
  }

  .logo-preview {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    border: 2px dashed #d9d9d9;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    overflow: hidden;
  }

  .logo-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .form-section {
    margin-bottom: 24px;
  }

  .section-header {
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  .button-group {
    display: flex;
    gap: 12px;
    margin-top: 24px;

    .save-button {
      background: #00d9a9;
      border-color: #00d9a9;

      &:hover {
        background: #01c49b !important;
        border-color: #01c49b !important;
      }
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
  }

  .social-links-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const industries = [
  "Information Technology",
  "Financial Services",
  "Healthcare",
  "Manufacturing",
  "Retail",
  "Education",
  "Consulting",
  "E-commerce",
  "Telecommunications",
  "Media & Entertainment",
  "Real Estate",
  "Energy",
  "Transportation",
  "Other",
];

const employeeCountRanges = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "5000+",
];

const MyCompanyTab = ({ refreshKey = 0 }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [showConnectedBanner, setShowConnectedBanner] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    setUserRole(role || "");
    if (refreshKey > 0) {
      setShowConnectedBanner(true);
    }
    fetchOrganization();
  }, [refreshKey]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const organizationName = localStorage.getItem("userOrganization");

      if (!organizationName) {
        message.info("No organization associated with your account");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post(
          API_CONST.GET_ORGANIZATION_BY_NAME,
          { name: organizationName },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data?.organization) {
          const org = response.data.organization;
          setOrganization(org);
          setCompletionPercentage(org.profileCompletionPercentage || 0);

          form.setFieldsValue({
            name: org.name,
            website: org.website,
            industry: org.industry,
            gstNumber: org.gstNumber,
            employeeCount: org.employeeCount,
            summary: org.summary,
            address: org.address,
            city: org.city,
            state: org.state,
            country: org.country,
            pincode: org.pincode,
            phone: org.phone,
            email: org.email,
            foundedYear: org.foundedYear,
            linkedin: org.socialLinks?.linkedin || "",
            twitter: org.socialLinks?.twitter || "",
            facebook: org.socialLinks?.facebook || "",
            instagram: org.socialLinks?.instagram || "",
          });

          if (org.logo) {
            fetchLogoUrl(org.logo);
          }
        }
      } catch (error) {
        if (error.response?.status === 404) {
          form.setFieldsValue({ name: organizationName });
        } else {
          console.error("Error fetching organization:", error);
          message.error("Failed to load company details");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogoUrl = async (logoPath) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: logoPath },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        setLogoUrl(response.data.downloadUrl);
      }
    } catch (error) {
      console.error("Error fetching logo:", error);
    }
  };

  const handleLogoUpload = async (info) => {
    const file = info.file;
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("Please upload an image file");
      return;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem("authToken");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "organization-logo");
      formData.append(
        "organizationId",
        organization?.id || "new"
      );

      const response = await axios.post(API_CONST.UPLOAD_DOCUMENT, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.objectPath) {
        setOrganization((prev) => ({
          ...prev,
          logo: response.data.objectPath,
        }));
        fetchLogoUrl(response.data.objectPath);
        message.success("Logo uploaded successfully");
      }
    } catch (error) {
      message.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);
      const token = localStorage.getItem("authToken");

      const payload = {
        name: values.name,
        website: values.website,
        industry: values.industry,
        gstNumber: values.gstNumber,
        employeeCount: values.employeeCount,
        summary: values.summary,
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country,
        pincode: values.pincode,
        phone: values.phone,
        email: values.email,
        foundedYear: values.foundedYear,
        logo: organization?.logo,
        socialLinks: {
          linkedin: values.linkedin,
          twitter: values.twitter,
          facebook: values.facebook,
          instagram: values.instagram,
        },
      };

      let response;
      if (organization?.id) {
        response = await axios.put(
          `${API_CONST.UPDATE_ORGANIZATION}/${organization.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.put(
          API_CONST.UPDATE_MY_ORGANIZATION,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (response.data?.organization) {
        setOrganization(response.data.organization);
        setCompletionPercentage(
          response.data.organization.profileCompletionPercentage || 0
        );
        message.success("Company details saved successfully!");
      }
    } catch (error) {
      console.error("Error saving organization:", error);
      message.error("Failed to save company details");
    } finally {
      setSaving(false);
    }
  };

  const isPartner = userRole === "partner";

  if (loading) {
    return (
      <TabWrapper>
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </TabWrapper>
    );
  }

  return (
    <TabWrapper>
      <h2 className="section-title">Company Details</h2>

      {showConnectedBanner && (
        <Alert
          message="Company connected!"
          description="Your company has been linked to your profile. You can now complete your company details below."
          type="success"
          showIcon
          closable
          onClose={() => setShowConnectedBanner(false)}
          style={{ marginBottom: 16 }}
        />
      )}

      {completionPercentage < 100 && (
        <div className="completion-banner">
          <Progress
            type="circle"
            percent={completionPercentage}
            width={50}
            strokeColor="#00d9a9"
          />
          <div className="completion-text">
            <div className="completion-title">Complete Your Company Profile</div>
            <div className="completion-subtitle">
              {isPartner
                ? "Fill in all required fields including GST, employee count, and company summary."
                : "Add your logo, website, and industry to complete your profile."}
            </div>
          </div>
        </div>
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="logo-upload">
          <div className="logo-preview">
            {uploadingLogo ? (
              <LoadingOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            ) : logoUrl ? (
              <img src={logoUrl} alt="Company Logo" />
            ) : (
              <BankOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
            )}
          </div>
          <Upload
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleLogoUpload}
            accept="image/*"
          >
            <Button icon={<UploadOutlined />} loading={uploadingLogo}>
              {logoUrl ? "Change Logo" : "Upload Logo"}
            </Button>
          </Upload>
        </div>

        <div className="form-section">
          <div className="section-header">Basic Information</div>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label="Company Name" name="name">
                <Input disabled placeholder="Company Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Website"
                name="website"
                rules={[{ type: "url", message: "Please enter a valid URL" }]}
              >
                <Input placeholder="https://example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Industry" name="industry">
                <Select placeholder="Select Industry">
                  {industries.map((ind) => (
                    <Option key={ind} value={ind}>
                      {ind}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Founded Year" name="foundedYear">
                <InputNumber
                  min={1900}
                  max={new Date().getFullYear()}
                  style={{ width: "100%" }}
                  placeholder="e.g., 2010"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="GST/Registration Number"
                name="gstNumber"
                rules={
                  isPartner
                    ? [{ required: true, message: "GST/Registration Number is required" }]
                    : []
                }
              >
                <Input placeholder="e.g., 22AAAAA0000A1Z5" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {isPartner && (
          <div className="form-section">
            <div className="section-header">Business Details</div>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item label="Employee Count" name="employeeCount">
                  <Select placeholder="Select Range">
                    {employeeCountRanges.map((range) => (
                      <Option key={range} value={range}>
                        {range}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Company Summary" name="summary">
                  <TextArea
                    rows={4}
                    placeholder="Brief description of your company..."
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        <div className="form-section">
          <div className="section-header">Contact Information</div>
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label="Address" name="address">
                <TextArea rows={2} placeholder="Street address" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="City" name="city">
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="State" name="state">
                <Input placeholder="State" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Pincode" name="pincode">
                <Input placeholder="Pincode" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Country" name="country">
                <Input placeholder="Country" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="+91-XXXXXXXXXX" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Email" name="email">
                <Input placeholder="company@example.com" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="form-section">
          <div className="section-header">Social Links</div>
          <div className="social-links-grid">
            <Form.Item label="LinkedIn" name="linkedin">
              <Input
                prefix={<LinkedinOutlined style={{ color: "#0077b5" }} />}
                placeholder="LinkedIn URL"
              />
            </Form.Item>
            <Form.Item label="Twitter" name="twitter">
              <Input
                prefix={<TwitterOutlined style={{ color: "#1da1f2" }} />}
                placeholder="Twitter URL"
              />
            </Form.Item>
            <Form.Item label="Facebook" name="facebook">
              <Input
                prefix={<FacebookOutlined style={{ color: "#1877f2" }} />}
                placeholder="Facebook URL"
              />
            </Form.Item>
            <Form.Item label="Instagram" name="instagram">
              <Input
                prefix={<InstagramOutlined style={{ color: "#e1306c" }} />}
                placeholder="Instagram URL"
              />
            </Form.Item>
          </div>
        </div>

        <Divider />

        <div className="button-group">
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saving}
            className="save-button"
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </TabWrapper>
  );
};

export default MyCompanyTab;
